import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, DatePicker, Input, InputNumber, message, Modal, Select, Space, Statistic, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { CheckCircleFilled, CopyOutlined } from '@ant-design/icons';
import request from '../../utils/request';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getCustomerList, getProjectDetail, getProjectList } from '../../services/project';
import {
  createPaymentOrder,
  executeSelectivePaymentOrder,
  fetchFundsAccounts,
  fetchInvoicePayableSummary,
  type FundsAccount,
  type InvoicePayableSummary,
} from '../../services/finance';

interface BillRow {
  id: number;
  system_code?: string;
  customer_id: number;
  customer_display?: string;
  project_id?: number | null;
  period_start: string;
  period_end: string;
  status: string;
  payment_status?: string;
  currency: string;
  subtotal?: number | string;
  total?: number | string;
  original_total?: number | string;
  adjusted_total?: number | string;
  paid_amount?: number | string;
  outstanding_amount?: number | string;
  due_date?: string;
  created_at?: string;
  lines?: Array<{
    id: number;
    billing_record?: number;
    description?: string;
    amount?: number | string;
    project_id?: number | null;
    project_name?: string | null;
  }>;
  project_summaries?: Array<{
    project_id?: number | null;
    project_code?: string;
    project_name?: string;
    record_count?: number;
    amount_total?: number | string;
  }>;
}

interface RuleSummaryRow {
  rule_key: string;
  rule_name?: string;
  rule_description?: string;
  record_count: number;
  amount_total: number;
}

interface ProjectRuleSummaryRow {
  project_id?: number | null;
  project_code?: string;
  project_name?: string;
  record_count: number;
  amount_total: number;
  rule_count: number;
  rules: RuleSummaryRow[];
}

interface InvoiceAdjustmentLineRow {
  id: number;
  action: string;
  after_amount?: number | string;
  delta_amount?: number | string;
  before_snapshot?: Record<string, any>;
  after_snapshot?: Record<string, any>;
}

interface InvoiceAdjustmentRow {
  id: number;
  adjustment_no: string;
  invoice: number;
  status: string;
  reason: string;
  operator_id?: number;
  applied_at?: string;
  created_at?: string;
  lines?: InvoiceAdjustmentLineRow[];
}

interface InvoiceSummaryPayload {
  invoice_count: number;
  original_total: string;
  adjust_delta_total: string;
  adjusted_total: string;
  paid_total: string;
  outstanding_total: string;
  customer_count?: number;
  currency_summaries?: Array<{
    currency: string;
    invoice_count: number;
    original_total: string;
    adjust_delta_total: string;
    adjusted_total: string;
    paid_total: string;
    outstanding_total: string;
    customer_count?: number;
  }>;
}

interface InvoicePaymentHistoryRow {
  payment_order_id: number;
  payment_order_no: string;
  ledger_entry_no?: string | null;
  amount_executed: string;
  running_paid_total: string;
  executed_at?: string | null;
  ledger_created_at?: string | null;
  remark?: string;
}

interface InvoicePaymentHistoryPayload {
  invoice_id: number;
  invoice_system_code: string;
  currency: string;
  adjusted_total: string;
  paid_amount: string;
  outstanding_amount: string;
  payment_count: number;
  records: InvoicePaymentHistoryRow[];
}

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  unpaid: '未支付',
  partial: '部分支付',
  paid: '已全额支付',
};

const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  USD: '$',
  CNY: '￥',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  HKD: 'HK$',
};

const formatCurrencyDisplay = (currency?: string) => {
  const code = String(currency || '').toUpperCase();
  if (!code) return '-';
  const symbol = CURRENCY_SYMBOL_MAP[code];
  return symbol ? `${code} ${symbol}` : code;
};

const BillsPage: React.FC = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const billsQuickJumpAppliedSigRef = useRef<string>('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<BillRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
  });
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [systemCode, setSystemCode] = useState<string>('');
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genCustomerId, setGenCustomerId] = useState<number | undefined>(undefined);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryTitle, setSummaryTitle] = useState('涉及项目明细');
  const [summaryRows, setSummaryRows] = useState<BillRow['project_summaries']>([]);
  const [summaryCurrency, setSummaryCurrency] = useState<string>('');
  const [summaryInvoiceCode, setSummaryInvoiceCode] = useState<string>('');
  const [summaryRecordCount, setSummaryRecordCount] = useState<number>(0);
  const [summaryAmountTotal, setSummaryAmountTotal] = useState<number>(0);
  const [summaryPage, setSummaryPage] = useState<number>(1);
  const [summaryPageSize, setSummaryPageSize] = useState<number>(10);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<BillRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ruleSummaryRows, setRuleSummaryRows] = useState<RuleSummaryRow[]>([]);
  const [ruleSummaryPage, setRuleSummaryPage] = useState<number>(1);
  const [ruleSummaryPageSize, setRuleSummaryPageSize] = useState<number>(10);
  const [projectRuleSummaryRows, setProjectRuleSummaryRows] = useState<ProjectRuleSummaryRow[]>([]);
  const [projectRulePage, setProjectRulePage] = useState<number>(1);
  const [projectRulePageSize, setProjectRulePageSize] = useState<number>(10);
  const [projectRuleExpandedKeys, setProjectRuleExpandedKeys] = useState<Array<string | number>>([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payRow, setPayRow] = useState<BillRow | null>(null);
  const [paySummary, setPaySummary] = useState<InvoicePayableSummary | null>(null);
  const [payAccounts, setPayAccounts] = useState<FundsAccount[]>([]);
  const [payAccountId, setPayAccountId] = useState<number | undefined>(undefined);
  const [payAmount, setPayAmount] = useState<number>(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editRow, setEditRow] = useState<BillRow | null>(null);
  const [editReason, setEditReason] = useState('');
  const [editAmountInvoice, setEditAmountInvoice] = useState<number | null>(null);
  const [editAdjustDirection, setEditAdjustDirection] = useState<'increase' | 'decrease'>('increase');
  const [editAdjustAmount, setEditAdjustAmount] = useState<number | null>(0);
  const [editRefProjectIds, setEditRefProjectIds] = useState<number[]>([]);
  const [editRefRuleKeys, setEditRefRuleKeys] = useState<string[]>([]);
  const [editRefRuleOptions, setEditRefRuleOptions] = useState<Array<{ value: string; label: string; name?: string }>>([]);
  const [adjustDetailOpen, setAdjustDetailOpen] = useState(false);
  const [adjustDetailInvoiceCode, setAdjustDetailInvoiceCode] = useState('');
  const [adjustDetailRows, setAdjustDetailRows] = useState<InvoiceAdjustmentRow[]>([]);
  const [adjustDetailLoading, setAdjustDetailLoading] = useState(false);
  const [adjustDetailPage, setAdjustDetailPage] = useState<number>(1);
  const [adjustDetailPageSize, setAdjustDetailPageSize] = useState<number>(8);
  const [adjustDetailOriginalTotal, setAdjustDetailOriginalTotal] = useState<number>(0);
  const [adjustDetailAdjustedTotal, setAdjustDetailAdjustedTotal] = useState<number>(0);
  const [summaryInfoOpen, setSummaryInfoOpen] = useState(false);
  const [summaryInfoLoading, setSummaryInfoLoading] = useState(false);
  const [summaryInfo, setSummaryInfo] = useState<InvoiceSummaryPayload | null>(null);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistorySummary, setPaymentHistorySummary] = useState<InvoicePaymentHistoryPayload | null>(null);
  const [paymentHistoryRows, setPaymentHistoryRows] = useState<InvoicePaymentHistoryRow[]>([]);
  const [paymentHistoryPage, setPaymentHistoryPage] = useState<number>(1);
  const [paymentHistoryPageSize, setPaymentHistoryPageSize] = useState<number>(8);
  const { RangePicker } = DatePicker;
  const currentLocale = useMemo(() => {
    const lang = String(i18n?.language || '').toLowerCase();
    if (lang.startsWith('zh')) return 'zh-CN';
    if (lang.startsWith('en')) return 'en-US';
    return undefined;
  }, [i18n?.language]);

  const sortedProjectSummaries = useMemo(() => {
    const list = Array.isArray(detailRow?.project_summaries) ? [...detailRow!.project_summaries!] : [];
    list.sort((a, b) => Number(b?.amount_total || 0) - Number(a?.amount_total || 0));
    return list;
  }, [detailRow]);

  const overviewProjectCount = sortedProjectSummaries.length;
  const overviewRuleCount = ruleSummaryRows.length;
  const overviewRecordCount = useMemo(() => {
    if (sortedProjectSummaries.length) {
      return sortedProjectSummaries.reduce((acc, it) => acc + Number(it?.record_count || 0), 0);
    }
    const lines = Array.isArray(detailRow?.lines) ? detailRow!.lines! : [];
    return new Set(lines.map((l) => Number(l?.billing_record)).filter((id) => Number.isFinite(id) && id > 0)).size;
  }, [sortedProjectSummaries, detailRow]);
  const overviewAmountTotal = useMemo(() => {
    if (sortedProjectSummaries.length) {
      return sortedProjectSummaries.reduce((acc, it) => acc + Number(it?.amount_total || 0), 0);
    }
    const lines = Array.isArray(detailRow?.lines) ? detailRow!.lines! : [];
    return lines.reduce((acc, it) => acc + Number(it?.amount || 0), 0);
  }, [sortedProjectSummaries, detailRow]);
  const selectedPayAccount = useMemo(
    () => payAccounts.find((a) => a.id === payAccountId) || null,
    [payAccounts, payAccountId],
  );
  const payOutstanding = Number(paySummary?.invoice_outstanding_amount || payRow?.outstanding_amount || 0);
  const payAvailable = Number(selectedPayAccount?.available_balance || 0);
  const payAmountValid = Number.isFinite(Number(payAmount)) && Number(payAmount) > 0 && Number(payAmount) <= payOutstanding + 1e-9;
  const payInsufficient = Number(payAmount || 0) > payAvailable + 1e-9;
  const payDisabled = payLoading || paySubmitting || !paySummary || !selectedPayAccount || !payAmountValid || payInsufficient;
  const editOriginalTotal = Number(editRow?.original_total ?? editRow?.total ?? 0);
  const editCurrentAdjustedTotal = Number(editRow?.adjusted_total ?? editRow?.total ?? 0);
  const editCurrentDelta = Number((editCurrentAdjustedTotal - editOriginalTotal).toFixed(2));
  const editTargetTotal = Number(editAmountInvoice || 0);
  const editTargetDelta = Number((editTargetTotal - editOriginalTotal).toFixed(2));
  const editProjectOptions = useMemo(() => {
    const list = Array.isArray(editRow?.project_summaries) ? [...(editRow?.project_summaries || [])] : [];
    return list.map((p) => {
      const code = String(p?.project_code || '').trim();
      const name = String(p?.project_name || '').trim();
      const nameLooksLikeId = /^\d+$/.test(name);
      const label = code && name && name !== code && !nameLooksLikeId ? `${code} - ${name}` : (code || (name && !nameLooksLikeId ? name : '未指定项目'));
      return { value: Number(p?.project_id || 0), label };
    });
  }, [editRow]);
  const editSelectedProjectLabels = useMemo(() => {
    const map = new Map<number, string>();
    editProjectOptions.forEach((x) => map.set(Number(x.value), x.label));
    return editRefProjectIds
      .map((id) => map.get(Number(id)) || `项目#${id}`)
      .filter(Boolean);
  }, [editProjectOptions, editRefProjectIds]);
  const editSelectedRuleLabels = useMemo(() => {
    const map = new Map<string, string>();
    editRefRuleOptions.forEach((x) => map.set(String(x.value), x.label));
    return editRefRuleKeys
      .map((k) => map.get(String(k)) || k)
      .filter(Boolean);
  }, [editRefRuleKeys, editRefRuleOptions]);
  const buildRefSummaryText = useMemo(() => {
    const format = (arr: string[]) => {
      if (!arr.length) return '无';
      if (arr.length <= 3) return arr.join('、');
      return `${arr.slice(0, 3).join('、')} 等${arr.length}条`;
    };
    return `涉及项目:${format(editSelectedProjectLabels)}  涉及收费规则:${format(editSelectedRuleLabels)}`;
  }, [editSelectedProjectLabels, editSelectedRuleLabels]);

  const isAdmin = useMemo(() => {
    const u: any = user || {};
    return !!(u?.is_staff || u?.is_superuser || u?.role === 'admin' || u?.roles?.includes?.('admin'));
  }, [user]);
  const applyBillsProjectQuickJump = (projectIdRaw: string, jumpTs?: string) => {
    const pid = Number(projectIdRaw);
    if (!Number.isFinite(pid) || pid <= 0) return;
    const sig = `${pid}|${String(jumpTs || '').trim()}`;
    if (billsQuickJumpAppliedSigRef.current === sig) return;
    setProjectId(pid);
    setPagination((p) => ({ ...p, current: 1 }));
    billsQuickJumpAppliedSigRef.current = sig;
  };
  const rechargePageUrl = useMemo(
    () => `${isAdmin ? '/dashboard/admin/finance/funds' : '/dashboard/customer/finance/funds'}?tab=recharge`,
    [isAdmin],
  );

  const copyText = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  const openSummaryModal = (row: BillRow) => {
    const list = Array.isArray(row.project_summaries) ? row.project_summaries : [];
    setSummaryRows(list);
    setSummaryTitle(`涉及项目明细 - ${row.system_code || `Invoice-${row.id}`}`);
    setSummaryCurrency(String(row.currency || ''));
    setSummaryInvoiceCode(row.system_code || `Invoice-${row.id}`);
    setSummaryRecordCount(list.reduce((acc, it) => acc + Number(it?.record_count || 0), 0));
    setSummaryAmountTotal(list.reduce((acc, it) => acc + Number(it?.amount_total || 0), 0));
    setSummaryPage(1);
    setSummaryPageSize(10);
    setSummaryOpen(true);
  };

  const downloadInvoice = (row: BillRow) => {
    (async () => {
      try {
        const params: any = {};
        if (currentLocale) params.locale = currentLocale;
        const resp = await request.get(`/api/v1/billing/invoices/${row.id}/export-detail-xlsx/`, {
          params,
          responseType: 'blob' as any,
        });
        const header = String(resp?.headers?.['content-disposition'] || '');
        const match = header.match(/filename=\"?([^\";]+)\"?/i);
        const fallback = `${row.system_code || `Invoice-${row.id}`}-明细.xlsx`;
        const fileName = decodeURIComponent((match?.[1] || '').trim() || fallback);
        const blob = new Blob([resp.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        message.success('账单明细已开始下载');
      } catch (e: any) {
        const detail = e?.response?.data?.detail || '下载失败';
        message.error(detail);
      }
    })();
  };

  const openPayModal = async (row: BillRow) => {
    setPayRow(row);
    setPayOpen(true);
    setPayLoading(true);
    setPaySummary(null);
    setPayAccounts([]);
    setPayAccountId(undefined);
    try {
      const [summary, accounts] = await Promise.all([
        fetchInvoicePayableSummary(row.id),
        fetchFundsAccounts({ customer_id: row.customer_id, currency: row.currency }),
      ]);
      setPaySummary(summary);
      setPayAccounts(accounts || []);
      const selected = (accounts || [])[0];
      setPayAccountId(selected?.id);
      setPayAmount(Number(summary?.invoice_outstanding_amount || row.outstanding_amount || 0));
      if (!(accounts || []).length) {
        message.warning('未找到匹配币种的资金账户，请先充值或创建资金账户');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载支付信息失败');
    } finally {
      setPayLoading(false);
    }
  };

  const submitPay = async () => {
    if (!payRow || !paySummary || !selectedPayAccount) return;
    const amount = Number(payAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      message.warning('请输入有效支付金额');
      return;
    }
    if (amount > payOutstanding + 1e-9) {
      message.warning(`本次支付金额不可超过未支付总额 ${payOutstanding.toFixed(2)}`);
      return;
    }
    if (amount > payAvailable + 1e-9) {
      message.warning('可用余额不足，请先充值再支付');
      return;
    }
    setPaySubmitting(true);
    try {
      const po = await createPaymentOrder({
        invoice: paySummary.invoice_id,
        customer_id: payRow.customer_id,
        currency: payRow.currency,
        amount_requested: amount,
        pay_mode: 'balance',
        remark: `from_bills_invoice_${paySummary.invoice_id}`,
      });
      await executeSelectivePaymentOrder(po.id, {
        amount,
      });
      message.success('支付成功');
      setPayOpen(false);
      setPayRow(null);
      setPaySummary(null);
      setPayAccounts([]);
      await fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '支付失败');
    } finally {
      setPaySubmitting(false);
    }
  };

  const openOverviewModal = async (row: BillRow) => {
    setDetailRow(row);
    setDetailOpen(true);
    setRuleSummaryRows([]);
    setProjectRuleSummaryRows([]);
    setRuleSummaryPage(1);
    setRuleSummaryPageSize(10);
    setProjectRulePage(1);
    setProjectRulePageSize(10);
    setProjectRuleExpandedKeys([]);
    setDetailLoading(true);
    try {
      const params: any = {};
      if (currentLocale) params.locale = currentLocale;
      const [ruleResp, projectRuleResp] = await Promise.all([
        request.get(`/api/v1/billing/invoices/${row.id}/overview-by-rule/`, { params }),
        request.get(`/api/v1/billing/invoices/${row.id}/overview-by-project-rule/`, { params }),
      ]);
      const list = Array.isArray(ruleResp?.data?.rows) ? ruleResp.data.rows : [];
      setRuleSummaryRows(
        list.map((it: any) => ({
          rule_key: String(it?.rule_key || ''),
          rule_name: String(it?.rule_name || '-'),
          rule_description: String(it?.rule_description || '-'),
          record_count: Number(it?.record_count || 0),
          amount_total: Number(it?.amount_total || 0),
        })),
      );
      const projectRuleList = Array.isArray(projectRuleResp?.data?.rows) ? projectRuleResp.data.rows : [];
      setProjectRuleSummaryRows(
        projectRuleList.map((p: any) => ({
          project_id: p?.project_id ?? null,
          project_code: String(p?.project_code || ''),
          project_name: String(p?.project_name || ''),
          record_count: Number(p?.record_count || 0),
          amount_total: Number(p?.amount_total || 0),
          rule_count: Number(p?.rule_count || 0),
          rules: (Array.isArray(p?.rules) ? p.rules : []).map((r: any) => ({
            rule_key: String(r?.rule_key || ''),
            rule_name: String(r?.rule_name || '-'),
            rule_description: String(r?.rule_description || '-'),
            record_count: Number(r?.record_count || 0),
            amount_total: Number(r?.amount_total || 0),
          })),
        })),
      );
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载账单概览失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const openPaymentHistoryModal = async (row: BillRow) => {
    setPaymentHistoryOpen(true);
    setPaymentHistoryLoading(true);
    setPaymentHistorySummary(null);
    setPaymentHistoryRows([]);
    setPaymentHistoryPage(1);
    setPaymentHistoryPageSize(8);
    try {
      const resp = await request.get<InvoicePaymentHistoryPayload>(`/api/v1/billing/invoices/${row.id}/payment-history/`);
      const payload = resp?.data as InvoicePaymentHistoryPayload;
      setPaymentHistorySummary(payload);
      setPaymentHistoryRows(Array.isArray(payload?.records) ? payload.records : []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载支付记录失败');
      setPaymentHistorySummary(null);
      setPaymentHistoryRows([]);
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const loadAdjustments = async (invoiceId: number) => {
    const resp = await request.get('/api/v1/billing/funds/invoice-adjustments/', {
      params: { invoice: invoiceId, page_size: 200 },
    });
    const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
    return (list || []) as InvoiceAdjustmentRow[];
  };

  const openAdjustDetailModal = async (row: BillRow) => {
    setAdjustDetailInvoiceCode(row.system_code || `Invoice-${row.id}`);
    setAdjustDetailOriginalTotal(Number(row.original_total ?? row.total ?? 0));
    setAdjustDetailAdjustedTotal(Number(row.adjusted_total ?? row.total ?? 0));
    setAdjustDetailOpen(true);
    setAdjustDetailPage(1);
    setAdjustDetailPageSize(8);
    setAdjustDetailLoading(true);
    try {
      const list = await loadAdjustments(row.id);
      setAdjustDetailRows(list);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载调整明细失败');
      setAdjustDetailRows([]);
    } finally {
      setAdjustDetailLoading(false);
    }
  };

  const openEditModal = async (row: BillRow) => {
    setEditRow(row);
    setEditReason('');
    setEditAmountInvoice(Number(row.adjusted_total ?? row.total ?? 0));
    setEditAdjustDirection('increase');
    setEditAdjustAmount(0);
    setEditRefProjectIds([]);
    setEditRefRuleKeys([]);
    setEditRefRuleOptions([]);
    setEditOpen(true);
    setEditSubmitting(false);
    try {
      const params: any = {};
      if (currentLocale) params.locale = currentLocale;
      const ruleResp = await request.get(`/api/v1/billing/invoices/${row.id}/overview-by-rule/`, { params });
      const ruleList = Array.isArray(ruleResp?.data?.rows) ? ruleResp.data.rows : [];
      setEditRefRuleOptions(
        ruleList.map((it: any) => ({
          value: String(it?.rule_key || ''),
          label: `${String(it?.rule_key || '')}${it?.rule_name ? ` - ${String(it?.rule_name)}` : ''}`,
          name: String(it?.rule_name || ''),
        })).filter((x) => !!x.value)
      );
    } catch {
      setEditRefRuleOptions([]);
    }
  };

  const submitEditAdjustment = async () => {
    if (!editRow) return;
    if (!editReason.trim()) {
      message.warning('请填写调整原因');
      return;
    }
    const currentAmount = Number(editRow.adjusted_total ?? editRow.total ?? 0);
    const targetAmount = Number(editAmountInvoice || 0);
    if (!Number.isFinite(targetAmount)) {
      message.warning('请输入有效目标金额');
      return;
    }
    const delta = Number((targetAmount - currentAmount).toFixed(6));
    if (Math.abs(delta) < 0.0000005) {
      message.info('目标金额与当前金额一致，无需调整');
      return;
    }
    const projectLabelMap = new Map<number, string>();
    editProjectOptions.forEach((x) => projectLabelMap.set(Number(x.value), x.label));
    const ruleLabelMap = new Map<string, string>();
    editRefRuleOptions.forEach((x) => ruleLabelMap.set(String(x.value), x.label));
    const scopePayload: Record<string, any> = {
      scope_type: 'invoice',
      ref_project_ids: editRefProjectIds,
      ref_project_labels: editRefProjectIds.map((id) => projectLabelMap.get(Number(id)) || `项目#${id}`),
      ref_rule_keys: editRefRuleKeys,
      ref_rule_labels: editRefRuleKeys.map((k) => ruleLabelMap.get(String(k)) || k),
    };
    setEditSubmitting(true);
    try {
      const adjResp = await request.post('/api/v1/billing/funds/invoice-adjustments/', {
        invoice: editRow.id,
        reason: `${buildRefSummaryText}${editReason.trim() ? `\n${editReason.trim()}` : ''}`,
      });
      const adjustmentId = Number(adjResp?.data?.id || 0);
      if (!adjustmentId) throw new Error('创建调整单失败');
      await request.post(`/api/v1/billing/funds/invoice-adjustments/${adjustmentId}/add_line/`, {
        action: 'add_line',
        after_amount: delta,
        invoice_line: null,
        after_snapshot: {
          ...scopePayload,
          before_amount: Number(currentAmount.toFixed(6)),
          after_amount: Number(targetAmount.toFixed(6)),
          delta_amount: Number(delta.toFixed(6)),
        },
      });
      await request.post(`/api/v1/billing/funds/invoice-adjustments/${adjustmentId}/apply/`);
      message.success('账单调整已应用');
      setEditOpen(false);
      await fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '账单调整失败');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleEditDirectionChange = (direction: 'increase' | 'decrease') => {
    setEditAdjustDirection(direction);
    const base = Number(editRow?.adjusted_total ?? editRow?.total ?? 0);
    const amt = Number(editAdjustAmount || 0);
    const target = direction === 'increase' ? base + amt : base - amt;
    setEditAmountInvoice(Number(target.toFixed(2)));
  };

  const handleEditAdjustAmountChange = (nextVal: number | null) => {
    const amt = typeof nextVal === 'number' && Number.isFinite(nextVal) ? Number(nextVal) : 0;
    setEditAdjustAmount(amt);
    const base = Number(editRow?.adjusted_total ?? editRow?.total ?? 0);
    const target = editAdjustDirection === 'increase' ? base + amt : base - amt;
    setEditAmountInvoice(Number(target.toFixed(2)));
  };

  const handleEditTargetAmountChange = (nextVal: number | null) => {
    const target = typeof nextVal === 'number' && Number.isFinite(nextVal) ? Number(nextVal) : 0;
    setEditAmountInvoice(target);
    const base = Number(editRow?.adjusted_total ?? editRow?.total ?? 0);
    const delta = Number((target - base).toFixed(2));
    if (delta >= 0) {
      setEditAdjustDirection('increase');
      setEditAdjustAmount(Number(Math.abs(delta).toFixed(2)));
    } else {
      setEditAdjustDirection('decrease');
      setEditAdjustAmount(Number(Math.abs(delta).toFixed(2)));
    }
  };

  const columns: ColumnsType<BillRow> = useMemo(() => {
    const base: ColumnsType<BillRow> = [
      {
        title: '账单系统编码',
        dataIndex: 'system_code',
        width: 300,
        render: (v: string, r) => {
          const code = v || `Invoice-${r.id}`;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>{code}</span>
              <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(code)} />
            </span>
          );
        },
      },
      { title: '账单周期', key: 'period', width: 220, render: (_: any, r) => `${r.period_start} ~ ${r.period_end}` },
      {
        title: '支付状态',
        dataIndex: 'payment_status',
        width: 140,
        render: (v: string) => {
          const key = v || 'unpaid';
          const color = key === 'paid' ? 'green' : key === 'partial' ? 'gold' : 'red';
          return (
            <Tag color={color} style={{ borderRadius: 999, paddingInline: 10 }}>
              {PAYMENT_STATUS_LABEL[key] || key || '-'}
            </Tag>
          );
        },
      },
      { title: '币种', dataIndex: 'currency', width: 88 },
      { title: '原始总额', dataIndex: 'total', width: 120, align: 'right', render: (v) => Number(v || 0).toFixed(2) },
      {
        title: '调整值',
        key: 'adjust_delta',
        width: 110,
        align: 'right',
        render: (_: any, r: BillRow) => {
          const original = Number(r.original_total ?? r.total ?? 0);
          const adjusted = Number(r.adjusted_total ?? r.total ?? 0);
          const delta = adjusted - original;
          if (Math.abs(delta) < 0.0000001) {
            return <span style={{ color: '#000' }}>0</span>;
          }
          if (delta > 0) {
            return (
              <span
                onClick={() => openAdjustDetailModal(r)}
                style={{
                  color: '#cf1322',
                  border: '1px solid #ffa39e',
                  background: '#fff1f0',
                  borderRadius: 12,
                  padding: '1px 8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {`+${delta.toFixed(2)}`}
              </span>
            );
          }
          return (
            <span
              onClick={() => openAdjustDetailModal(r)}
              style={{
                color: '#389e0d',
                border: '1px solid #b7eb8f',
                background: '#f6ffed',
                borderRadius: 12,
                padding: '1px 8px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {`${delta.toFixed(2)}`}
            </span>
          );
        },
      },
      { title: '应付总额', dataIndex: 'adjusted_total', width: 128, align: 'right', render: (v) => Number(v || 0).toFixed(2) },
      {
        title: '已支付',
        dataIndex: 'paid_amount',
        width: 120,
        align: 'right',
        render: (v, r) => {
          const paid = Number(v || 0);
          if (!(paid > 0)) return paid.toFixed(2);
          return (
            <Typography.Link onClick={() => openPaymentHistoryModal(r)}>
              {paid.toFixed(2)}
            </Typography.Link>
          );
        },
      },
      { title: '未支付', dataIndex: 'outstanding_amount', width: 110, align: 'right', render: (v) => Number(v || 0).toFixed(2) },
      { title: '到期日', dataIndex: 'due_date', width: 120, render: (v) => v || '-' },
      { title: '创建时间', dataIndex: 'created_at', width: 168, render: (v) => (v ? new Date(v).toLocaleString('zh-CN') : '-') },
      {
        title: '操作',
        key: 'actions',
        width: 280,
        fixed: 'right',
        render: (_: any, r: BillRow) => (
          <Space size={4}>
            <Button size="small" onClick={() => openOverviewModal(r)}>查看概览</Button>
            <Button size="small" onClick={() => downloadInvoice(r)}>下载明细</Button>
            {isAdmin && (
              <Button size="small" onClick={() => openEditModal(r)}>
                调整
              </Button>
            )}
            <Button
              size="small"
              type="primary"
              disabled={r.payment_status === 'paid'}
              onClick={() => openPayModal(r)}
            >
              支付
            </Button>
          </Space>
        ),
      },
    ];
    if (isAdmin) {
      base.splice(1, 0, {
        title: '所属客户',
        dataIndex: 'customer_display',
        width: 220,
        render: (v: string, r: BillRow) => v || String(r.customer_id || '-'),
      });
    }
    return base;
  }, [isAdmin]);

  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params: any = buildSummaryParams();
      params.page = page;
      params.page_size = pageSize;
      params.ordering = '-created_at';

      const resp = await request.get('/api/v1/billing/invoices/', { params });
      const list = Array.isArray(resp.data?.results) ? resp.data.results : [];
      setRows(list);
      setTotal(Number(resp.data?.count || 0));
    } finally {
      setLoading(false);
    }
  };

  const buildSummaryParams = () => {
    const params: any = {};
    if (isAdmin && customerId) params.customer_id = customerId;
    if (projectId) params.project_id = projectId;
    if (systemCode) params.system_code = systemCode;
    if (currency) params.currency = currency;
    if (paymentStatus) params.payment_status = paymentStatus;
    if (dateRange?.[0]) params.period_start = dateRange[0];
    if (dateRange?.[1]) params.period_end = dateRange[1];
    return params;
  };

  const openSummaryInfoModal = async () => {
    setSummaryInfoOpen(true);
    setSummaryInfoLoading(true);
    try {
      const resp = await request.get('/api/v1/billing/invoices/summary/', {
        params: buildSummaryParams(),
      });
      setSummaryInfo(resp.data as InvoiceSummaryPayload);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载汇总信息失败');
      setSummaryInfo(null);
    } finally {
      setSummaryInfoLoading(false);
    }
  };

  const openGenerateModal = () => {
    setGenCustomerId(customerId);
    searchCustomers('');
    setGenerateOpen(true);
  };

  const searchCustomers = async (kw?: string) => {
    setCustomerLoading(true);
    try {
      const list: any[] = (await getCustomerList({ onlyMain: true })) || [];
      const keyword = String(kw || '').trim().toLowerCase();
      const filtered = keyword
        ? list.filter((c: any) => {
            const nm = String(c?.name || c?.username || '').toLowerCase();
            const code = String(c?.customer_code || '').toLowerCase();
            const idText = String(c?.id || '').toLowerCase();
            return nm.includes(keyword) || code.includes(keyword) || idText.includes(keyword);
          })
        : list;
      const opts = filtered.map((c: any) => {
        const name = c?.name || c?.username || '';
        const code = c?.customer_code || '';
        const idText = c?.id || '';
        const label = code ? `${name} · ${code}（ID:${idText}）` : `${name}（ID:${idText}）`;
        return { value: Number(c.id), label };
      });
      setCustomerOptions(opts);
    } catch {
      setCustomerOptions([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  const searchProjects = async (kw?: string) => {
    setProjectLoading(true);
    try {
      const params: any = {
        page: 1,
        page_size: 100,
        search: kw || '',
      };
      if (isAdmin && customerId) params.customer = customerId;
      const resp: any = await getProjectList(params);
      const list: any[] = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      setProjectOptions(
        list.map((p: any) => ({
          value: Number(p.id),
          label: `${p.project_code || p.id} - ${p.name || '-'}`,
        })),
      );
    } catch {
      setProjectOptions([]);
    } finally {
      setProjectLoading(false);
    }
  };

  // 当项目筛选由外部跳转直接写入 projectId 时，若 options 尚未包含该项目，
  // 主动补齐该 option，避免下拉框仅显示 ID。
  useEffect(() => {
    const pid = Number(projectId);
    if (!Number.isFinite(pid) || pid <= 0) return;
    const exists = (projectOptions || []).some((opt) => Number(opt?.value) === pid);
    if (exists) return;
    let cancelled = false;
    (async () => {
      try {
        const resp: any = await getProjectDetail(pid);
        if (cancelled) return;
        const p = resp?.data || {};
        const option = {
          value: pid,
          label: `${p?.project_code || pid} - ${p?.name || '-'}`,
        };
        setProjectOptions((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((x) => Number(x?.value) === pid)) return list;
          return [option, ...list];
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, projectOptions]);

  const handleGenerateInvoice = async () => {
    if (!genCustomerId) {
      message.warning('请填写 customer_id');
      return;
    }
    setGenerating(true);
    try {
      const payload: any = { customer_id: genCustomerId };
      const resp = await request.post('/api/v1/billing/invoices/generate_for_customer/', payload);
      const createdCount = Number(resp?.data?.created_invoice_count || 0);
      const boundCount = Number(resp?.data?.bound_record_count || 0);
      const codeList: string[] = Array.isArray(resp?.data?.created_invoice_system_codes)
        ? resp.data.created_invoice_system_codes.filter(Boolean)
        : [];
      const codeText = codeList.length ? `；编码：${codeList.join('，')}` : '';
      message.success(`账单生成成功：生成 ${createdCount} 张，绑定 ${boundCount} 条收费记录${codeText}`);
      setGenerateOpen(false);
      fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || '账单生成失败';
      message.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current as number, pagination.pageSize as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, customerId, projectId, systemCode, currency, paymentStatus, dateRange, isAdmin]);

  // 项目列表快捷跳转：仅覆盖项目筛选，保持其余筛选状态逻辑不变
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const fromProjectList = String(params.get('from_project_list') || '').trim() === '1';
      const quickProjectId = String(params.get('filter_project') || '').trim();
      const jumpTs = String(params.get('jump_ts') || '').trim();
      if (!fromProjectList || !quickProjectId) return;
      applyBillsProjectQuickJump(quickProjectId, jumpTs);

      // 清理一次性跳转参数，保留其余参数（含 tabId）
      params.delete('from_project_list');
      params.delete('filter_project');
      params.delete('jump_ts');
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 兜底桥接：当 URL 参数被 tab 状态恢复覆盖时，仍可应用项目筛选
  useEffect(() => {
    const consumeBridge = () => {
      try {
        const raw = sessionStorage.getItem('bills_project_quick_jump');
        if (!raw) return;
        const data = JSON.parse(raw || '{}') as { projectId?: string; jumpTs?: string };
        if (!data?.projectId) return;
        applyBillsProjectQuickJump(String(data.projectId), String(data.jumpTs || ''));
        sessionStorage.removeItem('bills_project_quick_jump');
      } catch {}
    };
    consumeBridge();
    const handler = (ev: any) => {
      try {
        const d = (ev as CustomEvent)?.detail || {};
        if (!d?.projectId) return;
        applyBillsProjectQuickJump(String(d.projectId), String(d.jumpTs || ''));
      } catch {}
    };
    try { window.addEventListener('bills:project-quick-jump' as any, handler as any); } catch {}
    return () => {
      try { window.removeEventListener('bills:project-quick-jump' as any, handler as any); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="p-6">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>账单</Typography.Title>
        <Space>
          {isAdmin && (
            <Button type="primary" onClick={openGenerateModal}>
              手动触发生成账单
            </Button>
          )}
          <Button onClick={openSummaryInfoModal} loading={summaryInfoLoading}>
            查看汇总信息
          </Button>
          <Button onClick={() => fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20)} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      <Space wrap style={{ marginBottom: 12 }}>
        {isAdmin && (
          <Space size={4}>
            <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>客户ID：</span>
            <InputNumber
              placeholder="客户ID"
              style={{ width: 140 }}
              value={customerId}
              onChange={(v) => setCustomerId(typeof v === 'number' ? v : undefined)}
            />
          </Space>
        )}
        <Space size={4}>
          <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>项目：</span>
          <Select
            showSearch
            allowClear
            filterOption={false}
            placeholder="包含项目（按项目名/编码搜索）"
            style={{ width: 260 }}
            options={projectOptions}
            loading={projectLoading}
            value={projectId}
            onFocus={() => searchProjects('')}
            onSearch={(v) => searchProjects(v)}
            onChange={(v) => setProjectId(typeof v === 'number' ? v : undefined)}
          />
        </Space>
        <Space size={4}>
          <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>系统编码：</span>
          <Input
            allowClear
            placeholder="系统编码"
            style={{ width: 320 }}
            value={systemCode}
            onChange={(e) => setSystemCode(e.target.value)}
          />
        </Space>
        <Space size={4}>
          <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>币种：</span>
          <Select
            allowClear
            placeholder="币种"
            style={{ width: 120 }}
            value={currency}
            onChange={(v) => setCurrency(v)}
            options={[
              { value: 'USD', label: 'USD' },
              { value: 'CNY', label: 'CNY' },
              { value: 'EUR', label: 'EUR' },
            ]}
          />
        </Space>
        <Space size={4}>
          <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>支付状态：</span>
          <Select
            allowClear
            placeholder="支付状态"
            style={{ width: 140 }}
            value={paymentStatus}
            onChange={(v) => setPaymentStatus(v)}
            options={[
              { value: 'unpaid', label: '未支付' },
              { value: 'partial', label: '部分支付' },
              { value: 'paid', label: '已全额支付' },
            ]}
          />
        </Space>
        <Space size={4}>
          <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>周期：</span>
          <RangePicker
            allowClear
            value={[
              dateRange?.[0] ? dayjs(dateRange[0]) : null,
              dateRange?.[1] ? dayjs(dateRange[1]) : null,
            ] as any}
            onChange={(vals) => {
              if (vals && vals.length === 2) {
                setDateRange([vals[0]?.format('YYYY-MM-DD'), vals[1]?.format('YYYY-MM-DD')]);
              } else {
                setDateRange([undefined, undefined]);
              }
            }}
            placeholder={['周期开始', '周期结束']}
          />
        </Space>
        <Button onClick={() => {
          setCustomerId(undefined);
          setProjectId(undefined);
          setSystemCode('');
          setCurrency(undefined);
          setPaymentStatus(undefined);
          setDateRange([undefined, undefined]);
          setPagination((p) => ({ ...p, current: 1 }));
        }}>
          重置
        </Button>
        <Button
          type="primary"
          loading={loading}
          onClick={() => fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20)}
        >
          搜索
        </Button>
      </Space>

      <Table<BillRow>
        rowKey={(r) => String(r.id)}
        columns={columns}
        dataSource={rows}
        loading={loading}
        onRow={(record) => ({
          style: record.payment_status === 'paid' ? { backgroundColor: '#f6ffed' } : {},
        })}
        scroll={{ x: 1900 }}
        pagination={{
          ...pagination,
          total,
          showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
          onChange: (current, pageSize) => setPagination((p) => ({ ...p, current, pageSize })),
        }}
      />

      <Modal
        open={paymentHistoryOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>账单支付记录</span>
            <span style={{ color: '#1677ff', fontWeight: 600 }}>
              {paymentHistorySummary?.invoice_system_code || '-'}
            </span>
            <CopyOutlined
              style={{ color: '#1677ff', cursor: 'pointer' }}
              onClick={() => copyText(paymentHistorySummary?.invoice_system_code || '')}
            />
          </div>
        )}
        footer={null}
        width={1080}
        onCancel={() => setPaymentHistoryOpen(false)}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap size={24}>
            <Statistic title="币种" value={formatCurrencyDisplay(paymentHistorySummary?.currency)} />
            <Statistic title="应付总额" value={Number(paymentHistorySummary?.adjusted_total || 0)} precision={2} />
            <Statistic title="已支付总额" value={Number(paymentHistorySummary?.paid_amount || 0)} precision={2} />
            <Statistic title="未支付" value={Number(paymentHistorySummary?.outstanding_amount || 0)} precision={2} />
            <Statistic title="支付次数" value={Number(paymentHistorySummary?.payment_count || 0)} />
          </Space>
          <Table<InvoicePaymentHistoryRow>
            rowKey={(r) => `${r.payment_order_id}-${r.ledger_entry_no || 'none'}`}
            loading={paymentHistoryLoading}
            size="small"
            pagination={{
              current: paymentHistoryPage,
              pageSize: paymentHistoryPageSize,
              showSizeChanger: true,
              showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
              onChange: (page, pageSize) => {
                setPaymentHistoryPage(page);
                setPaymentHistoryPageSize(pageSize || 8);
              },
              onShowSizeChange: (_current, size) => {
                setPaymentHistoryPage(1);
                setPaymentHistoryPageSize(size || 8);
              },
            }}
            dataSource={paymentHistoryRows}
            columns={[
              { title: '支付单号', dataIndex: 'payment_order_no', width: 200 },
              {
                title: '交易流水号',
                dataIndex: 'ledger_entry_no',
                width: 240,
                render: (v?: string | null) => (v ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Typography.Text code>{v}</Typography.Text>
                    <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(v)} />
                  </span>
                ) : '-'),
              },
              { title: '本次支付金额', dataIndex: 'amount_executed', align: 'right', width: 140, render: (v) => Number(v || 0).toFixed(2) },
              { title: '累计已支付', dataIndex: 'running_paid_total', align: 'right', width: 140, render: (v) => Number(v || 0).toFixed(2) },
              { title: '支付时间', dataIndex: 'executed_at', width: 180, render: (v) => (v ? new Date(v).toLocaleString('zh-CN') : '-') },
              { title: '备注', dataIndex: 'remark', ellipsis: true, render: (v?: string) => v || '-' },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>涉及项目明细</span>
            <span style={{ color: '#1677ff', fontWeight: 600 }}>
              {summaryInvoiceCode || '-'}
            </span>
            <CopyOutlined
              style={{ color: '#1677ff', cursor: 'pointer' }}
              onClick={() => copyText(summaryInvoiceCode)}
            />
            <span style={{ color: '#666', marginLeft: 30 }}>
              {`共${Array.isArray(summaryRows) ? summaryRows.length : 0}个项目 ${summaryRecordCount}条收费记录 金额:${summaryCurrency || ''} ${summaryAmountTotal.toFixed(2)}`}
            </span>
          </div>
        )}
        open={summaryOpen}
        footer={null}
        width={1120}
        onCancel={() => setSummaryOpen(false)}
      >
        <Table
          rowKey={(r: any, idx?: number) => `${r?.project_id ?? 'none'}-${idx || 0}`}
          size="small"
          pagination={{
            current: summaryPage,
            pageSize: summaryPageSize,
            showSizeChanger: true,
            showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
            onChange: (page, pageSize) => {
              setSummaryPage(page);
              setSummaryPageSize(pageSize || 10);
            },
            onShowSizeChange: (_current, size) => {
              setSummaryPage(1);
              setSummaryPageSize(size || 10);
            },
          }}
          dataSource={Array.isArray(summaryRows) ? summaryRows : []}
          columns={[
            {
              title: '项目',
              dataIndex: 'project_name',
              render: (_: any, r: any) => {
                const code = String(r?.project_code || '').trim();
                const name = String(r?.project_name || '').trim();
                const nameLooksLikeId = /^\d+$/.test(name);
                if (code && name && name !== code && !nameLooksLikeId) return `${code} - ${name}`;
                if (code) return code;
                if (name && !nameLooksLikeId) return name;
                return '-';
              },
            },
            { title: '币种', dataIndex: 'currency', width: 100, render: () => summaryCurrency || '-' },
            { title: '收费总额', dataIndex: 'amount_total', align: 'right', render: (v: any) => Number(v || 0).toFixed(2) },
            { title: '收费记录总数', dataIndex: 'record_count', align: 'right', render: (v: any) => Number(v || 0) },
          ]}
        />
      </Modal>

      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingRight: 36 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span>账单概览</span>
              <span style={{ color: '#1677ff', fontWeight: 600 }}>
                {detailRow?.system_code || (detailRow ? `Invoice-${detailRow.id}` : '-')}
              </span>
              <CopyOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={() => copyText(detailRow?.system_code || (detailRow ? `Invoice-${detailRow.id}` : ''))}
              />
            </div>
            <span style={{ color: '#666' }}>
              {`涉及项目${overviewProjectCount}个 收费规则${overviewRuleCount}种 收费记录${overviewRecordCount}条 总金额(${detailRow?.currency || '-'}) ${overviewAmountTotal.toFixed(2)}`}
            </span>
          </div>
        )}
        open={detailOpen}
        footer={null}
        width={1120}
        onCancel={() => setDetailOpen(false)}
      >
        <Tabs
          defaultActiveKey="project"
          items={[
            {
              key: 'project',
              label: '按项目维度',
              children: (
                <Table
                  rowKey={(r: any, idx?: number) => `${r?.project_id ?? 'none'}-${idx || 0}`}
                  size="small"
                  pagination={{
                    current: summaryPage,
                    pageSize: summaryPageSize,
                    showSizeChanger: true,
                    showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
                    onChange: (page, pageSize) => {
                      setSummaryPage(page);
                      setSummaryPageSize(pageSize || 10);
                    },
                    onShowSizeChange: (_current, size) => {
                      setSummaryPage(1);
                      setSummaryPageSize(size || 10);
                    },
                  }}
                  dataSource={sortedProjectSummaries}
                  columns={[
                    {
                      title: '项目',
                      dataIndex: 'project_name',
                      render: (_: any, r: any) => {
                        const code = String(r?.project_code || '').trim();
                        const name = String(r?.project_name || '').trim();
                        const nameLooksLikeId = /^\d+$/.test(name);
                        if (code && name && name !== code && !nameLooksLikeId) return `${code} - ${name}`;
                        if (code) return code;
                        if (name && !nameLooksLikeId) return name;
                        return '-';
                      },
                    },
                    { title: '币种', dataIndex: 'currency', width: 100, render: () => detailRow?.currency || '-' },
                    { title: '收费总额', dataIndex: 'amount_total', align: 'right', render: (v: any) => Number(v || 0).toFixed(2) },
                    { title: '收费记录总数', dataIndex: 'record_count', align: 'right', render: (v: any) => Number(v || 0) },
                  ]}
                />
              ),
            },
            {
              key: 'rule',
              label: '按收费规则维度',
              children: (
                <Table
                  rowKey={(r: RuleSummaryRow) => r.rule_key}
                  loading={detailLoading}
                  size="small"
                  pagination={{
                    current: ruleSummaryPage,
                    pageSize: ruleSummaryPageSize,
                    showSizeChanger: true,
                    showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
                    onChange: (page, pageSize) => {
                      setRuleSummaryPage(page);
                      setRuleSummaryPageSize(pageSize || 10);
                    },
                    onShowSizeChange: (_current, size) => {
                      setRuleSummaryPage(1);
                      setRuleSummaryPageSize(size || 10);
                    },
                  }}
                  dataSource={ruleSummaryRows}
                  columns={[
                    {
                      title: '收费规则系统编码',
                      dataIndex: 'rule_key',
                      width: 220,
                      render: (v: string) => (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span>{v || '-'}</span>
                          {v ? (
                            <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(v)} />
                          ) : null}
                        </span>
                      ),
                    },
                    { title: '收费项目名称', dataIndex: 'rule_name', width: 220, render: (v: any) => v || '-' },
                    {
                      title: '说明',
                      dataIndex: 'rule_description',
                      width: 340,
                      render: (v: any) => <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</div>,
                    },
                    { title: '币种', dataIndex: 'currency', width: 100, render: () => detailRow?.currency || '-' },
                    { title: '收费总额', dataIndex: 'amount_total', align: 'right', render: (v: any) => Number(v || 0).toFixed(2) },
                    { title: '收费记录总数', dataIndex: 'record_count', align: 'right', render: (v: any) => Number(v || 0) },
                  ]}
                />
              ),
            },
            {
              key: 'project-rule',
              label: '按项目+收费规则维度',
              children: (
                <Table<ProjectRuleSummaryRow>
                  rowKey={(r) => `${r.project_id ?? 'none'}|${String(r.project_code || '').trim()}|${String(r.project_name || '').trim()}`}
                  loading={detailLoading}
                  size="small"
                  onRow={(record) => {
                    const key = `${record.project_id ?? 'none'}|${String(record.project_code || '').trim()}|${String(record.project_name || '').trim()}`;
                    return projectRuleExpandedKeys.includes(key)
                      ? { style: { background: '#f0f7ff' } }
                      : {};
                  }}
                  pagination={{
                    current: projectRulePage,
                    pageSize: projectRulePageSize,
                    showSizeChanger: true,
                    showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
                    onChange: (page, pageSize) => {
                      setProjectRulePage(page);
                      setProjectRulePageSize(pageSize || 10);
                    },
                    onShowSizeChange: (_current, size) => {
                      setProjectRulePage(1);
                      setProjectRulePageSize(size || 10);
                    },
                  }}
                  dataSource={[...projectRuleSummaryRows].sort((a, b) => Number(b.amount_total || 0) - Number(a.amount_total || 0))}
                  expandable={{
                    expandedRowKeys: projectRuleExpandedKeys,
                    onExpand: (expanded, record) => {
                      const key = `${record.project_id ?? 'none'}|${String(record.project_code || '').trim()}|${String(record.project_name || '').trim()}`;
                      setProjectRuleExpandedKeys((prev) => {
                        if (expanded) return Array.from(new Set([...prev, key]));
                        return prev.filter((k) => k !== key);
                      });
                    },
                    expandedRowRender: (project) => (
                      <div
                        style={{
                          marginLeft: 20,
                          padding: '10px 12px 12px',
                          borderLeft: '3px solid #91caff',
                          background: '#f7fbff',
                          borderRadius: 6,
                        }}
                      >
                        <div style={{ color: '#667085', fontSize: 12, marginBottom: 8 }}>
                          项目内收费规则（按收费总额降序）
                        </div>
                        <Table<RuleSummaryRow>
                          rowKey={(r) => r.rule_key}
                          size="small"
                          pagination={false}
                          dataSource={[...(project.rules || [])].sort((a, b) => Number(b.amount_total || 0) - Number(a.amount_total || 0))}
                          columns={[
                            {
                              title: '收费规则系统编码',
                              dataIndex: 'rule_key',
                              width: 220,
                              onHeaderCell: () => ({ style: { background: '#edf3fb', color: '#667085', fontWeight: 500 } }),
                              render: (v: string) => (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <span>{v || '-'}</span>
                                  {v ? (
                                    <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(v)} />
                                  ) : null}
                                </span>
                              ),
                            },
                            {
                              title: '收费项目名称',
                              dataIndex: 'rule_name',
                              width: 220,
                              onHeaderCell: () => ({ style: { background: '#edf3fb', color: '#667085', fontWeight: 500 } }),
                              render: (v: any) => v || '-',
                            },
                            {
                              title: '说明',
                              dataIndex: 'rule_description',
                              width: 340,
                              onHeaderCell: () => ({ style: { background: '#edf3fb', color: '#667085', fontWeight: 500 } }),
                              render: (v: any) => <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</div>,
                            },
                            {
                              title: '币种',
                              width: 100,
                              align: 'center',
                              onHeaderCell: () => ({ style: { background: '#edf3fb', color: '#667085', fontWeight: 500 } }),
                              render: () => detailRow?.currency || '-',
                            },
                            {
                              title: '收费总额',
                              dataIndex: 'amount_total',
                              width: 140,
                              align: 'right',
                              onHeaderCell: () => ({ style: { background: '#edf3fb', color: '#667085', fontWeight: 500 } }),
                              render: (v: any) => Number(v || 0).toFixed(2),
                            },
                            {
                              title: '收费记录总数',
                              dataIndex: 'record_count',
                              width: 140,
                              align: 'center',
                              onHeaderCell: () => ({ style: { background: '#edf3fb', color: '#667085', fontWeight: 500 } }),
                              render: (v: any) => Number(v || 0),
                            },
                          ]}
                        />
                      </div>
                    ),
                  }}
                  columns={[
                    {
                      title: '项目',
                      dataIndex: 'project_name',
                      width: 360,
                      render: (_: any, r: ProjectRuleSummaryRow) => {
                        const code = String(r?.project_code || '').trim();
                        const name = String(r?.project_name || '').trim();
                        const nameLooksLikeId = /^\d+$/.test(name);
                        const text = (code && name && name !== code && !nameLooksLikeId)
                          ? `${code} - ${name}`
                          : (code || (name && !nameLooksLikeId ? name : '-'));
                        return <span style={{ fontWeight: 600 }}>{text}</span>;
                      },
                    },
                    { title: '币种', width: 100, align: 'center', render: () => detailRow?.currency || '-' },
                    { title: '收费总额', dataIndex: 'amount_total', width: 160, align: 'right', render: (v: any) => Number(v || 0).toFixed(2) },
                    { title: '收费记录总数', dataIndex: 'record_count', width: 140, align: 'center', render: (v: any) => Number(v || 0) },
                    { title: '涉及规则数', dataIndex: 'rule_count', align: 'center', width: 120, render: (v: any) => Number(v || 0) },
                  ]}
                />
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 32, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span>账单调整明细</span>
              <span style={{ color: '#1677ff', fontWeight: 600 }}>
                {adjustDetailInvoiceCode || '-'}
              </span>
              <CopyOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={() => copyText(adjustDetailInvoiceCode)}
              />
            </span>
            <span style={{ color: '#666' }}>
              {`原始总额 ${adjustDetailOriginalTotal.toFixed(2)}  累计调整值 ${(adjustDetailAdjustedTotal - adjustDetailOriginalTotal >= 0 ? '+' : '')}${(adjustDetailAdjustedTotal - adjustDetailOriginalTotal).toFixed(2)}  应付总额 ${adjustDetailAdjustedTotal.toFixed(2)}`}
            </span>
          </div>
        )}
        open={adjustDetailOpen}
        footer={null}
        width={1100}
        onCancel={() => setAdjustDetailOpen(false)}
      >
        <Table<InvoiceAdjustmentRow>
          rowKey={(r) => String(r.id)}
          loading={adjustDetailLoading}
          size="small"
          pagination={{
            current: adjustDetailPage,
            pageSize: adjustDetailPageSize,
            showSizeChanger: true,
            showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
            onChange: (page, pageSize) => {
              setAdjustDetailPage(page);
              setAdjustDetailPageSize(pageSize || 8);
            },
            onShowSizeChange: (_current, size) => {
              setAdjustDetailPage(1);
              setAdjustDetailPageSize(size || 8);
            },
          }}
          dataSource={adjustDetailRows}
          columns={[
            { title: '调整单号', dataIndex: 'adjustment_no', width: 180 },
            {
              title: '调整值',
              key: 'delta',
              width: 120,
              align: 'right',
              render: (_: any, r) => {
                const delta = (r.lines || []).reduce((acc, x) => acc + Number(x?.delta_amount || 0), 0);
                if (delta > 0) return <span style={{ color: '#cf1322' }}>{`+${delta.toFixed(2)}`}</span>;
                if (delta < 0) return <span style={{ color: '#389e0d' }}>{delta.toFixed(2)}</span>;
                return '0.00';
              },
            },
            {
              title: '备注',
              dataIndex: 'reason',
              render: (v: any) => (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {String(v || '-')}
                </div>
              ),
            },
            { title: '创建时间', dataIndex: 'created_at', width: 170, render: (v: any) => (v ? new Date(v).toLocaleString('zh-CN') : '-') },
          ]}
        />
      </Modal>

      <Modal
        title={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>调整账单</span>
            <span style={{ color: '#1677ff', fontWeight: 600 }}>
              {editRow?.system_code || (editRow ? `Invoice-${editRow.id}` : '-')}
            </span>
            <CopyOutlined
              style={{ color: '#1677ff', cursor: 'pointer' }}
              onClick={() => copyText(editRow?.system_code || (editRow ? `Invoice-${editRow.id}` : ''))}
            />
          </span>
        )}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={submitEditAdjustment}
        okText="确认调整"
        confirmLoading={editSubmitting}
        width={980}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, alignItems: 'end' }}>
            <Statistic title="原始总额（不变）" value={editOriginalTotal} precision={2} />
            <Statistic
              title="已调整值"
              value={editCurrentDelta}
              precision={2}
              valueStyle={{ color: editCurrentDelta > 0 ? '#cf1322' : editCurrentDelta < 0 ? '#389e0d' : undefined }}
              formatter={(v) => {
                const n = Number(v || 0);
                if (n > 0) return `+${n.toFixed(2)}`;
                return n.toFixed(2);
              }}
            />
            <Statistic title="当前应付额" value={editCurrentAdjustedTotal} precision={2} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '180px 200px minmax(240px, 1fr) 180px', gap: 12, alignItems: 'end' }}>
            <div>
              <Typography.Text>调整方向</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={editAdjustDirection}
                options={[
                  { value: 'increase', label: '增加+' },
                  { value: 'decrease', label: '减少-' },
                ]}
                onChange={(v) => handleEditDirectionChange(v as 'increase' | 'decrease')}
              />
            </div>
            <div>
              <Typography.Text>本次调整金额</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                precision={2}
                step={0.01}
                min={0}
                value={editAdjustAmount}
                onChange={(v) => handleEditAdjustAmountChange(typeof v === 'number' ? v : null)}
                placeholder="输入调整金额"
              />
            </div>
            <div>
              <Typography.Text>调整后目标应付额</Typography.Text>
              <InputNumber
                style={{
                  width: '100%',
                  marginTop: 8,
                  background: '#fffbe6',
                  borderColor: '#ffe58f',
                  color: '#cf1322',
                  fontWeight: 700,
                }}
                precision={2}
                step={0.01}
                value={editAmountInvoice}
                onChange={(v) => handleEditTargetAmountChange(typeof v === 'number' ? v : null)}
                placeholder="输入调整后目标金额"
              />
            </div>
            <div>
              <Typography.Text>本次调整后总调整额</Typography.Text>
              <div style={{ marginTop: 8 }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontWeight: 700,
                    color: editTargetDelta > 0 ? '#cf1322' : editTargetDelta < 0 ? '#389e0d' : '#000',
                    border: editTargetDelta > 0 ? '1px solid #ffa39e' : editTargetDelta < 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9',
                    background: editTargetDelta > 0 ? '#fff1f0' : editTargetDelta < 0 ? '#f6ffed' : '#fafafa',
                  }}
                >
                  {editTargetDelta > 0 ? `+${editTargetDelta.toFixed(2)}` : editTargetDelta.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <Space wrap>
            <Typography.Text type="secondary">可选：关联项目（仅记录备注，不参与计算）</Typography.Text>
            <Select
              mode="multiple"
              showSearch
              style={{ width: 360 }}
              value={editRefProjectIds}
              options={editProjectOptions}
              onChange={(v) => setEditRefProjectIds(Array.isArray(v) ? v.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : [])}
              placeholder="选择项目（可选）"
            />
          </Space>
          <Space wrap>
            <Typography.Text type="secondary">可选：关联收费规则（仅记录备注，不参与计算）</Typography.Text>
            <Select
              mode="multiple"
              showSearch
              style={{ width: 360 }}
              value={editRefRuleKeys}
              options={editRefRuleOptions}
              onChange={(v) => setEditRefRuleKeys(Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : [])}
              placeholder="选择收费规则（可选）"
            />
          </Space>
          <div>
            <Typography.Text>备注</Typography.Text>
            <Input.TextArea
              rows={2}
              style={{ marginTop: 6 }}
              value={buildRefSummaryText}
              readOnly
            />
            <Input.TextArea
              rows={3}
              style={{ marginTop: 6 }}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="请填写补充说明（可选）"
            />
          </div>
          <Alert
            type="info"
            showIcon
            message="调整仅生成记录并影响账单调整后金额；原始账单明细与下载明细保持不变。"
          />
        </Space>
      </Modal>

      <Modal
        title="手动触发生成账单"
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={handleGenerateInvoice}
        okText="生成"
        confirmLoading={generating}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Typography.Text type="secondary">
            说明：仅按已闭合账期生成账单；系统将自动按“客户 + 账期 + 币种”分组，并汇总该客户名下所有项目的未出账收费记录。未闭合账期（如当月16日至当前日）不会在本次生成，将在下一个账期闭合后自动纳入生成。
          </Typography.Text>
          <Select
            showSearch
            allowClear
            filterOption={false}
            placeholder="选择客户（可按客户名/编码/ID搜索）"
            style={{ width: '100%' }}
            options={customerOptions}
            loading={customerLoading}
            value={genCustomerId}
            onFocus={() => searchCustomers('')}
            onSearch={(v) => searchCustomers(v)}
            onChange={(v) => setGenCustomerId(typeof v === 'number' ? v : undefined)}
          />
        </Space>
      </Modal>

      <Modal
        title="账单汇总信息"
        open={summaryInfoOpen}
        footer={null}
        width={980}
        onCancel={() => setSummaryInfoOpen(false)}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {(summaryInfo?.currency_summaries || []).map((item) => (
            <Card key={item.currency} size="small" title={`${item.currency} 汇总`}>
              <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? 'repeat(4, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                <Statistic title="账单数量" value={Number(item.invoice_count || 0)} />
                {isAdmin && <Statistic title="客户数量" value={Number(item.customer_count || 0)} />}
                <Statistic title="原始总额" value={Number(item.original_total || 0)} precision={2} />
                <Statistic
                  title="调整额"
                  value={Number(item.adjust_delta_total || 0)}
                  precision={2}
                  valueStyle={{
                    color: Number(item.adjust_delta_total || 0) > 0
                      ? '#cf1322'
                      : Number(item.adjust_delta_total || 0) < 0
                        ? '#389e0d'
                        : undefined,
                  }}
                  formatter={(v) => {
                    const n = Number(v || 0);
                    return n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
                  }}
                />
                <Statistic title="应付总额" value={Number(item.adjusted_total || 0)} precision={2} />
                <Statistic title="已支付" value={Number(item.paid_total || 0)} precision={2} />
                <Statistic title="未支付" value={Number(item.outstanding_total || 0)} precision={2} />
              </div>
            </Card>
          ))}
          {!(summaryInfo?.currency_summaries || []).length && (
            <Alert type="info" showIcon message="当前筛选条件下暂无账单数据" />
          )}
        </Space>
      </Modal>

      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>账单支付</span>
            <span style={{ color: '#1677ff', fontWeight: 600 }}>
              {payRow?.system_code || (payRow ? `Invoice-${payRow.id}` : '-')}
            </span>
            <CopyOutlined
              style={{ color: '#1677ff', cursor: 'pointer' }}
              onClick={() => copyText(payRow?.system_code || (payRow ? `Invoice-${payRow.id}` : ''))}
            />
          </div>
        )}
        open={payOpen}
        onCancel={() => setPayOpen(false)}
        width={980}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setPayOpen(false)}>
            取消
          </Button>,
          <Button key="ok" type="primary" loading={paySubmitting} disabled={payDisabled} onClick={submitPay}>
            确认支付
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? 'repeat(4, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            {isAdmin && (
              <Statistic title="所属客户" value={payRow?.customer_display || String(payRow?.customer_id || '-')} />
            )}
            <Statistic title="账单币种" value={formatCurrencyDisplay(payRow?.currency)} />
            <Statistic title="原始总额" value={Number(payRow?.total || 0)} precision={2} />
            <Statistic title="调整值" value={Number((Number(payRow?.adjusted_total || payRow?.total || 0) - Number(payRow?.original_total ?? payRow?.total ?? 0)).toFixed(2))} precision={2} />
            <Statistic title="调整后应付总额" value={Number(payRow?.adjusted_total || payRow?.total || 0)} precision={2} />
            <Statistic title="已支付" value={Number(payRow?.paid_amount || 0)} precision={2} />
            <Statistic title="未支付总额" value={payOutstanding} precision={2} valueStyle={{ color: '#cf1322' }} />
            <Statistic title="可用余额" value={payAvailable} precision={2} valueStyle={{ color: '#3f8600' }} />
          </div>

          <Space wrap>
            <span style={{ color: '#666' }}>付款账户</span>
            <Select
              style={{ width: 360 }}
              value={payAccountId}
              loading={payLoading}
              options={payAccounts.map((a) => ({
                value: a.id,
                label: `${formatCurrencyDisplay(a.currency)}（可用 ${Number(a.available_balance || 0).toFixed(2)}）`,
              }))}
              onChange={(v) => setPayAccountId(v)}
              placeholder="请选择付款账户"
            />
            <span style={{ color: '#666', marginLeft: 8 }}>本次支付</span>
            <InputNumber
              min={0}
              max={payOutstanding}
              step={0.01}
              precision={2}
              style={{ width: 180 }}
              value={payAmount}
              onChange={(v) => {
                const n = Number(v || 0);
                if (!Number.isFinite(n)) {
                  setPayAmount(0);
                  return;
                }
                if (n > payOutstanding) {
                  setPayAmount(Number(payOutstanding.toFixed(2)));
                  return;
                }
                setPayAmount(Number(n.toFixed(2)));
              }}
            />
            <span style={{ color: '#666' }}>{formatCurrencyDisplay(payRow?.currency)}</span>
          </Space>

          {!payAmountValid && (
            <Alert type="warning" showIcon message={`本次支付金额需在 0 ~ ${payOutstanding.toFixed(2)} 之间`} />
          )}
          {!payInsufficient && payAmountValid && Number(payAmount || 0) > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#389e0d', fontWeight: 600 }}>
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <span>可用余额充足，可顺利支付</span>
            </div>
          )}
          {payInsufficient && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Alert
                type="error"
                showIcon
                message="可用余额不足，请先充值再支付"
                style={{ width: 380, maxWidth: '100%' }}
              />
              <Button
                type="primary"
                onClick={() => {
                  window.location.href = rechargePageUrl;
                }}
              >
                充值
              </Button>
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default BillsPage;
